import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Send, MessageCircle, Plus, Trash2, Save, Zap } from 'lucide-react';
import api from '@/utils/api';

const emptyTg = { label: '', bot_token: '', chat_id: '', enabled: true };
const emptyWa = { label: '', invite_link: '', enabled: true };

const BroadcastSettings = () => {
  const [tgGroups, setTgGroups] = useState([]);
  const [waCommunities, setWaCommunities] = useState([]);
  const [tgDraft, setTgDraft] = useState(emptyTg);
  const [waDraft, setWaDraft] = useState(emptyWa);
  const [savingTg, setSavingTg] = useState(false);
  const [savingWa, setSavingWa] = useState(false);
  const [testingId, setTestingId] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [t, w] = await Promise.all([
        api.get('/cms/telegram-groups'),
        api.get('/cms/whatsapp-communities'),
      ]);
      setTgGroups(t.data?.data || []);
      setWaCommunities(w.data?.data || []);
    } catch (e) {
      toast.error('Failed to load broadcast settings');
    }
  };

  // ==================== TELEGRAM ====================
  const addTelegramGroup = async () => {
    if (!tgDraft.label || !tgDraft.bot_token || !tgDraft.chat_id) {
      toast.error('Label, Bot Token and Chat ID are required');
      return;
    }
    setSavingTg(true);
    try {
      await api.post('/cms/telegram-groups', tgDraft);
      toast.success('Telegram group added');
      setTgDraft(emptyTg);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add group');
    } finally {
      setSavingTg(false);
    }
  };

  const updateTelegramGroup = async (id, patch) => {
    try {
      await api.put(`/cms/telegram-groups/${id}`, patch);
      setTgGroups(prev => prev.map(g => g.id === id ? { ...g, ...patch } : g));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update group');
      fetchAll();
    }
  };

  const deleteTelegramGroup = async (id) => {
    if (!window.confirm('Delete this Telegram group?')) return;
    try {
      await api.delete(`/cms/telegram-groups/${id}`);
      toast.success('Group deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const testTelegramGroup = async (id) => {
    setTestingId(id);
    try {
      const r = await api.post(`/cms/telegram-groups/${id}/test`);
      if (r.data?.success) toast.success('Test message delivered to Telegram!');
      else toast.error(`Telegram test failed: ${r.data?.error || 'unknown error'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally {
      setTestingId(null);
    }
  };

  // ==================== WHATSAPP ====================
  const addWaCommunity = async () => {
    if (!waDraft.label) {
      toast.error('Label is required');
      return;
    }
    setSavingWa(true);
    try {
      await api.post('/cms/whatsapp-communities', waDraft);
      toast.success('WhatsApp community added');
      setWaDraft(emptyWa);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally {
      setSavingWa(false);
    }
  };

  const updateWaCommunity = async (id, patch) => {
    try {
      await api.put(`/cms/whatsapp-communities/${id}`, patch);
      setWaCommunities(prev => prev.map(g => g.id === id ? { ...g, ...patch } : g));
    } catch (e) {
      toast.error('Failed to update');
      fetchAll();
    }
  };

  const deleteWaCommunity = async (id) => {
    if (!window.confirm('Delete this community?')) return;
    try {
      await api.delete(`/cms/whatsapp-communities/${id}`);
      toast.success('Community deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="broadcast-settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Broadcast Settings</h1>
        <p className="text-slate-600 mt-1">Manage Telegram groups (auto-broadcast) & WhatsApp communities (manual share)</p>
      </div>

      {/* TELEGRAM GROUPS */}
      <Card className="p-6 mb-6 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-sky-600" />
            Telegram Groups
            <span className="text-xs text-slate-500 font-normal">(approved jobs are auto-posted to all enabled groups)</span>
          </h2>
        </div>

        {/* Add new */}
        <div className="grid md:grid-cols-4 gap-3 p-4 mb-5 rounded-lg border border-dashed border-slate-300 bg-slate-50">
          <Input data-testid="tg-label-input" placeholder="Label (e.g. VVVSI Job Alerts)"
                 value={tgDraft.label} onChange={(e) => setTgDraft({ ...tgDraft, label: e.target.value })} />
          <Input data-testid="tg-token-input" placeholder="Bot Token (12345:ABC...)"
                 value={tgDraft.bot_token} onChange={(e) => setTgDraft({ ...tgDraft, bot_token: e.target.value })} />
          <Input data-testid="tg-chatid-input" placeholder="Chat ID (-1001234...)"
                 value={tgDraft.chat_id} onChange={(e) => setTgDraft({ ...tgDraft, chat_id: e.target.value })} />
          <Button data-testid="tg-add-btn" onClick={addTelegramGroup} disabled={savingTg}>
            <Plus className="w-4 h-4 mr-1" /> {savingTg ? 'Adding...' : 'Add Group'}
          </Button>
        </div>

        {/* List */}
        {tgGroups.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No Telegram groups configured yet.</p>
        ) : (
          <div className="space-y-3">
            {tgGroups.map((g) => (
              <div key={g.id} data-testid={`tg-group-row-${g.id}`}
                   className="grid md:grid-cols-12 gap-3 p-4 bg-white border rounded-lg items-center">
                <Input className="md:col-span-3" value={g.label}
                       onChange={(e) => setTgGroups(prev => prev.map(x => x.id === g.id ? { ...x, label: e.target.value } : x))}
                       onBlur={() => updateTelegramGroup(g.id, { label: g.label })} />
                <Input className="md:col-span-4 font-mono text-xs" value={g.bot_token}
                       onChange={(e) => setTgGroups(prev => prev.map(x => x.id === g.id ? { ...x, bot_token: e.target.value } : x))}
                       onBlur={() => updateTelegramGroup(g.id, { bot_token: g.bot_token })} />
                <Input className="md:col-span-2 font-mono text-xs" value={g.chat_id}
                       onChange={(e) => setTgGroups(prev => prev.map(x => x.id === g.id ? { ...x, chat_id: e.target.value } : x))}
                       onBlur={() => updateTelegramGroup(g.id, { chat_id: g.chat_id })} />
                <div className="md:col-span-1 flex justify-center">
                  <Switch data-testid={`tg-toggle-${g.id}`} checked={!!g.enabled}
                          onCheckedChange={(v) => updateTelegramGroup(g.id, { enabled: v })} />
                </div>
                <div className="md:col-span-2 flex gap-2 justify-end">
                  <Button data-testid={`tg-test-${g.id}`} variant="outline" size="sm"
                          onClick={() => testTelegramGroup(g.id)} disabled={testingId === g.id}>
                    <Zap className="w-3.5 h-3.5 mr-1" /> {testingId === g.id ? 'Testing...' : 'Test'}
                  </Button>
                  <Button data-testid={`tg-delete-${g.id}`} variant="ghost" size="sm"
                          onClick={() => deleteTelegramGroup(g.id)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* WHATSAPP COMMUNITIES */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            WhatsApp Communities
            <span className="text-xs text-slate-500 font-normal">(used by the manual "Share to WhatsApp" button)</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-3 p-4 mb-5 rounded-lg border border-dashed border-slate-300 bg-slate-50">
          <Input data-testid="wa-label-input" placeholder="Label (e.g. Tamil Nadu Jobs)"
                 value={waDraft.label} onChange={(e) => setWaDraft({ ...waDraft, label: e.target.value })} />
          <Input data-testid="wa-link-input" placeholder="Invite link (https://chat.whatsapp.com/...)"
                 value={waDraft.invite_link} onChange={(e) => setWaDraft({ ...waDraft, invite_link: e.target.value })} />
          <Button data-testid="wa-add-btn" onClick={addWaCommunity} disabled={savingWa}>
            <Plus className="w-4 h-4 mr-1" /> {savingWa ? 'Adding...' : 'Add Community'}
          </Button>
        </div>

        {waCommunities.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No WhatsApp communities configured yet.</p>
        ) : (
          <div className="space-y-3">
            {waCommunities.map((c) => (
              <div key={c.id} data-testid={`wa-community-row-${c.id}`}
                   className="grid md:grid-cols-12 gap-3 p-4 bg-white border rounded-lg items-center">
                <Input className="md:col-span-3" value={c.label}
                       onChange={(e) => setWaCommunities(prev => prev.map(x => x.id === c.id ? { ...x, label: e.target.value } : x))}
                       onBlur={() => updateWaCommunity(c.id, { label: c.label })} />
                <Input className="md:col-span-7 text-xs" value={c.invite_link || ''}
                       onChange={(e) => setWaCommunities(prev => prev.map(x => x.id === c.id ? { ...x, invite_link: e.target.value } : x))}
                       onBlur={() => updateWaCommunity(c.id, { invite_link: c.invite_link })} />
                <div className="md:col-span-1 flex justify-center">
                  <Switch data-testid={`wa-toggle-${c.id}`} checked={!!c.enabled}
                          onCheckedChange={(v) => updateWaCommunity(c.id, { enabled: v })} />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button data-testid={`wa-delete-${c.id}`} variant="ghost" size="sm"
                          onClick={() => deleteWaCommunity(c.id)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default BroadcastSettings;
